import { ApiService, type ApiRequestConfig } from "./api.service";
import type {
  TIssue,
  TIssueActivity,
  TIssueDisplayProperties,
  TIssueLink,
  TIssueListResponse,
  TDependencySubtype,
  TIssueMeta,
  TIssuePredecessor,
  TIssueQuery,
  TIssueRelation,
  TIssueRelationResponse,
  TIssueRelationTypes,
  TIssueSubIssues,
} from "@/types/issue";
import type { TGanttDependency, TGanttDependencyType } from "@/types/gantt";
import type { TWorkItemDatesResponse } from "@/types/work-item-dates";
import type { TDependentDateShift } from "@/types/dependent-date-shift";
import type { TBulkOperationStatus, TBulkSelection } from "@/types/bulk-selection";
import type {
  TSubItemConversionPreview,
  TSubItemConversionQueued,
} from "@/types/sub-item-conversion";
import type { ExportStatus } from "@/components/data-table/use-csv-export";
import type {
  TWorkItemImportCommitResult,
  TWorkItemImportDefinition,
  TWorkItemImportDryRunResult,
  TWorkItemImportRowPayload,
} from "@/types/work-item-import";

function buildListQuery(params: TIssueQuery): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value == null) continue;
    if (Array.isArray(value)) {
      for (const v of value) search.append(key, String(v));
    } else {
      search.set(key, String(value));
    }
  }
  const str = search.toString();
  return str ? `?${str}` : "";
}

const ISSUE_RELATION_TYPES = [
  "blocks",
  "blocked_by",
  "relates_to",
] as const satisfies readonly TIssueRelationTypes[];

function emptyIssueRelations(): TIssueRelation {
  return ISSUE_RELATION_TYPES.reduce((acc, type) => {
    acc[type] = [];
    return acc;
  }, {} as TIssueRelation);
}

function normalizeIssueRelations(response: TIssueRelationResponse): TIssueRelation {
  const normalized = emptyIssueRelations();
  if (Array.isArray(response)) {
    for (const relation of response) {
      // Store consumers pass id to item-based removal and inverse-edge writes.
      // The API keeps id as the edge ID and supplies the peer separately.
      // Preserve legacy responses during parallel FE/BE deployment; dropping
      // their ID would break existing links before the new backend is available.
      normalized[relation.relation_type]?.push({
        ...relation,
        relation_id: relation.id,
        id: relation.related_work_item_id ?? relation.id,
      });
    }
    return normalized;
  }

  for (const type of ISSUE_RELATION_TYPES) {
    normalized[type] = Array.isArray(response[type]) ? response[type] : [];
  }
  return normalized;
}

export class WorkItemService extends ApiService {
  async listWorkItems(
    slug: string,
    projectId: string,
    query?: TIssueQuery,
    config?: ApiRequestConfig,
  ): Promise<TIssueListResponse> {
    return this.get(`/api/workspaces/${slug}/projects/${projectId}/work-items/`, {
      ...config,
      params: query,
    });
  }

  async listIssues(
    slug: string,
    projectId: string,
    query?: TIssueQuery,
  ): Promise<TIssueListResponse> {
    return this.listWorkItems(slug, projectId, query);
  }

  async listWorkspaceWorkItems(
    slug: string,
    query?: TIssueQuery,
    config?: ApiRequestConfig,
  ): Promise<TIssueListResponse> {
    return this.get(`/api/workspaces/${slug}/work-items/`, { ...config, params: query });
  }

  async listWorkspaceIssues(slug: string, query?: TIssueQuery): Promise<TIssueListResponse> {
    return this.listWorkspaceWorkItems(slug, query);
  }

  /**
   * Export the workspace work-items list as CSV, scoped to the same
   * filters/sort as the list (`params`) and, when `body.ids` is set,
   * restricted to those rows.  Returns the raw Response
   * (200 text/csv stream | 202 async token).
   */
  async exportWorkItems(
    slug: string,
    body: { ids?: string[]; columns?: string[] },
    params: TIssueQuery = {},
  ): Promise<Response> {
    return this.postRaw(
      `/api/workspaces/${slug}/export-work-items/${buildListQuery(params)}`,
      body,
    );
  }

  async getExportStatus(slug: string, token: string): Promise<ExportStatus> {
    return this.get(`/api/workspaces/${slug}/export-status/${token}/`);
  }

  /** What a dependency picker may offer: this project's tasks only (NG-1268).
   *  `openOnly` leaves out done and cancelled items, which the server refuses
   *  for a new blocking dependency. */
  async searchDependencyCandidates(
    slug: string,
    projectId: string,
    search: string,
    { openOnly }: { openOnly: boolean },
  ): Promise<TIssue[]> {
    return this.get(`/api/workspaces/${slug}/projects/${projectId}/search-work-items/`, {
      params: openOnly ? { search, open: true } : { search },
    });
  }

  async createWorkItem(slug: string, projectId: string, data: Partial<TIssue>): Promise<TIssue> {
    return this.post(`/api/workspaces/${slug}/projects/${projectId}/work-items/`, data);
  }

  async createIssue(slug: string, projectId: string, data: Partial<TIssue>): Promise<TIssue> {
    return this.createWorkItem(slug, projectId, data);
  }

  /**
   * Create a work item with **no project** — a standalone event (#3754).
   * Events-only server-side: a task/milestone subtype row requires a project.
   * Takes the same payload shape as `createWorkItem`, so the caller doesn't
   * build a second contract for the project-less case.
   */
  async createWorkspaceWorkItem(slug: string, data: Partial<TIssue>): Promise<TIssue> {
    return this.post(`/api/workspaces/${slug}/work-items/`, data);
  }

  async getWorkItem(slug: string, projectId: string, issueId: string): Promise<TIssue> {
    return this.get(`/api/workspaces/${slug}/projects/${projectId}/work-items/${issueId}/`);
  }

  async getIssue(slug: string, projectId: string, issueId: string): Promise<TIssue> {
    return this.getWorkItem(slug, projectId, issueId);
  }

  async updateWorkItem(
    slug: string,
    projectId: string,
    issueId: string,
    data: Partial<TIssue>,
  ): Promise<TIssue> {
    return this.patch(`/api/workspaces/${slug}/projects/${projectId}/work-items/${issueId}/`, data);
  }

  async updateIssue(
    slug: string,
    projectId: string,
    issueId: string,
    data: Partial<TIssue>,
  ): Promise<TIssue> {
    return this.updateWorkItem(slug, projectId, issueId, data);
  }

  /**
   * Update a work item that has no project via the workspace-scoped route.
   * Project work items must use {@link updateWorkItem} (project-scoped) so
   * project-level RBAC applies; this route only resolves project-less items.
   */
  async updateWorkspaceWorkItem(
    slug: string,
    issueId: string,
    data: Partial<TIssue>,
  ): Promise<TIssue> {
    return this.patch(`/api/workspaces/${slug}/work-items/${issueId}/`, data);
  }

  /**
   * Every delete here takes the item's subtasks with it (`cascade_children`,
   * NG-3560), so each caller must say so in its confirmation first — see
   * `components/issues/delete-sub-items.ts`.
   */
  async deleteWorkItem(slug: string, projectId: string, issueId: string): Promise<void> {
    return this.delete(
      `/api/workspaces/${slug}/projects/${projectId}/work-items/${issueId}/?cascade_children=true`,
    );
  }

  async deleteIssue(slug: string, projectId: string, issueId: string): Promise<void> {
    return this.deleteWorkItem(slug, projectId, issueId);
  }

  async bulkDeleteWorkItems(
    slug: string,
    projectId: string,
    issueIds: string[],
  ): Promise<{ success: boolean }> {
    return this.delete(
      `/api/workspaces/${slug}/projects/${projectId}/bulk-delete-work-items/?cascade_children=true`,
      { data: { issue_ids: issueIds } },
    );
  }

  async bulkDeleteIssues(
    slug: string,
    projectId: string,
    issueIds: string[],
  ): Promise<{ success: boolean }> {
    return this.bulkDeleteWorkItems(slug, projectId, issueIds);
  }

  /**
   * Delete work items selected from a workspace-wide list (Tasks/Events span
   * projects). Takes the selection envelope (included / all_but_excluded / all)
   * so "select all matching" needn't ship every id. Returns the raw Response:
   * `200 {deleted, skipped}` for a small selection, or `202 {token, total,
   * skipped}` when the server hands off to a background job. The server
   * authorizes `workitem.delete` per affected project and only deletes the
   * subset the actor may delete (the rest come back as `skipped`).
   */
  async bulkDeleteWorkspaceWorkItems(
    slug: string,
    selection: TBulkSelection<TIssueQuery>,
  ): Promise<Response> {
    return this.deleteRaw(`/api/workspaces/${slug}/bulk-delete-work-items/?cascade_children=true`, {
      selection,
    });
  }

  /**
   * How many subtasks deleting `selection` would also delete (NG-3560):
   * sub-sub-items and archived ones included, a ticked subtask and a family the
   * caller couldn't delete whole left out. Changes nothing.
   */
  async previewBulkDelete(slug: string, selection: TBulkSelection<TIssueQuery>): Promise<number> {
    const body: { sub_item_count: number } = await this.post(
      `/api/workspaces/${slug}/bulk-delete-work-items/preview/`,
      { selection },
    );
    return body.sub_item_count;
  }

  /**
   * Archive work items selected from a workspace-wide list. Same selection
   * envelope + sync/async split as {@link bulkDeleteWorkspaceWorkItems}, but
   * authorized per project on `workitem.edit`: `200 {archived, skipped,
   * skipped_reasons}` inline, or `202 {token, total, skipped}` when the server
   * hands off to a background job.
   */
  async bulkArchiveWorkspaceWorkItems(
    slug: string,
    selection: TBulkSelection<TIssueQuery>,
  ): Promise<Response> {
    return this.postRaw(`/api/workspaces/${slug}/bulk-archive-work-items/?cascade_children=true`, {
      selection,
    });
  }

  /**
   * Set the status of work items selected from a workspace-wide list. Same
   * selection envelope + sync/async split as
   * {@link bulkArchiveWorkspaceWorkItems}, plus the target workspace `state`
   * id: `200 {updated, skipped, skipped_reasons}` inline, or `202 {token,
   * total, skipped}` when the server hands off to a background job.
   */
  async bulkSetStatusWorkspaceWorkItems(
    slug: string,
    selection: TBulkSelection<TIssueQuery>,
    stateId: string,
  ): Promise<Response> {
    return this.postRaw(`/api/workspaces/${slug}/bulk-set-status-work-items/`, {
      selection,
      state: stateId,
    });
  }

  /**
   * Assign or unassign one user across work items selected from a
   * workspace-wide list — the tri-state picker's one-checkbox-click contract
   * (`assign=true` adds the user everywhere the selection reaches, `false`
   * removes them). Same selection envelope + sync/async split as
   * {@link bulkSetStatusWorkspaceWorkItems}.
   */
  async bulkSetAssigneesWorkspaceWorkItems(
    slug: string,
    selection: TBulkSelection<TIssueQuery>,
    userId: string,
    assign: boolean,
  ): Promise<Response> {
    return this.postRaw(`/api/workspaces/${slug}/bulk-set-assignees-work-items/`, {
      selection,
      user_id: userId,
      assign,
    });
  }

  /**
   * Set, or with `null` clear, the Stage of work items selected from a list
   * (NG-3466). Same selection envelope + sync/async split as
   * {@link bulkSetStatusWorkspaceWorkItems}. A stage the workspace doesn't hold,
   * or a retired one, refuses the whole request (400).
   */
  async bulkSetStageWorkspaceWorkItems(
    slug: string,
    selection: TBulkSelection<TIssueQuery>,
    stageId: string | null,
  ): Promise<Response> {
    return this.postRaw(`/api/workspaces/${slug}/bulk-set-stage-work-items/`, {
      selection,
      stage: stageId,
    });
  }

  /**
   * Add qualifications to work items selected from a list (NG-3466): each item
   * keeps what it has and gains only what it lacks. Needs "Assign
   * qualifications" at the workspace (403 otherwise) as well as edit rights on
   * each item. Same selection envelope + sync/async split as
   * {@link bulkSetStatusWorkspaceWorkItems}.
   */
  async bulkAddQualificationsWorkspaceWorkItems(
    slug: string,
    selection: TBulkSelection<TIssueQuery>,
    skillIds: string[],
  ): Promise<Response> {
    return this.postRaw(`/api/workspaces/${slug}/bulk-add-qualifications-work-items/`, {
      selection,
      skill_ids: skillIds,
    });
  }

  /** What converting *issueId* into a sub-item of *parentId* would move
   *  (NG-3471), for the confirmation. Writes nothing, and refuses (400) exactly
   *  as the convert would. */
  async getSubItemConversionPreview(
    slug: string,
    projectId: string,
    issueId: string,
    parentId: string,
    request?: ApiRequestConfig,
  ): Promise<TSubItemConversionPreview> {
    return this.get<TSubItemConversionPreview>(
      `/api/workspaces/${slug}/projects/${projectId}/work-items/${issueId}/convert-to-sub-item/`,
      { ...request, params: { parent_id: parentId } },
    );
  }

  /** Convert *issueId* into a sub-item of *parentId*, its sub-items with it, all
   *  or nothing (NG-3471): the item as the PATCH answers it, or, past 20 moving
   *  sub-items, a queued job whose token polls `getBulkOperationStatus`. */
  async convertToSubItem(
    slug: string,
    projectId: string,
    issueId: string,
    parentId: string,
  ): Promise<TIssue | TSubItemConversionQueued> {
    return this.post(
      `/api/workspaces/${slug}/projects/${projectId}/work-items/${issueId}/convert-to-sub-item/`,
      { parent_id: parentId },
    );
  }

  /** Poll a long-running bulk operation's progress by token. */
  async getBulkOperationStatus(slug: string, token: string): Promise<TBulkOperationStatus> {
    return this.get(`/api/workspaces/${slug}/bulk-operations/${token}/`);
  }

  /** Request cancellation of a long-running bulk operation by token — same
   *  domain-agnostic endpoint family as the status poll above. Flips a
   *  QUEUED/PROCESSING operation to CANCELLING; the worker finishes the batch
   *  it's mid-transaction on, then moves the row to CANCELLED itself. */
  async cancelBulkOperation(
    slug: string,
    token: string,
  ): Promise<{ token: string; status: string }> {
    return this.post(`/api/workspaces/${slug}/bulk-operations/${token}/cancel/`, {});
  }

  async updateWorkItemDates(
    slug: string,
    projectId: string,
    updates: Array<{ id: string; start_date?: string | null; target_date?: string | null }>,
  ): Promise<TWorkItemDatesResponse> {
    return this.post(`/api/workspaces/${slug}/projects/${projectId}/work-item-dates/`, {
      updates,
    });
  }

  /** The linked tasks a date change already saved on *workItemId* would move,
   *  and their shifted dates (NG-1283). Read-only: moving them is a
   *  `updateWorkItemDates` call with the rows the caller chooses. */
  async getDependentDateShift(
    slug: string,
    projectId: string,
    workItemId: string,
    params: { field: "start_date" | "target_date"; previous: string },
  ): Promise<TDependentDateShift> {
    return this.get<TDependentDateShift>(
      `/api/workspaces/${slug}/projects/${projectId}/work-items/${workItemId}/dependent-date-shift/`,
      { params },
    );
  }

  async updateIssueDates(
    slug: string,
    projectId: string,
    updates: Array<{ id: string; start_date?: string | null; target_date?: string | null }>,
  ): Promise<TWorkItemDatesResponse> {
    return this.updateWorkItemDates(slug, projectId, updates);
  }

  async getDisplayProperties(
    slug: string,
    projectId: string,
    config?: ApiRequestConfig,
  ): Promise<TIssueDisplayProperties> {
    return this.get(`/api/workspaces/${slug}/projects/${projectId}/user-properties/`, config);
  }

  async updateDisplayProperties(
    slug: string,
    projectId: string,
    properties: Partial<TIssueDisplayProperties>,
  ): Promise<TIssueDisplayProperties> {
    return this.post(`/api/workspaces/${slug}/projects/${projectId}/user-properties/`, properties);
  }

  async getWorkItemMeta(slug: string, projectId: string, issueId: string): Promise<TIssueMeta> {
    return this.get(`/api/workspaces/${slug}/projects/${projectId}/work-items/${issueId}/meta/`);
  }

  async getIssueMeta(slug: string, projectId: string, issueId: string): Promise<TIssueMeta> {
    return this.getWorkItemMeta(slug, projectId, issueId);
  }

  async getWorkItemByIdentifier(
    slug: string,
    workItem: string,
    config?: ApiRequestConfig,
  ): Promise<TIssue> {
    return this.get(`/api/workspaces/${slug}/work-items/${workItem}/`, config);
  }

  async getIssueByIdentifier(slug: string, workItem: string): Promise<TIssue> {
    return this.getWorkItemByIdentifier(slug, workItem);
  }

  async getWorkItemActivity(
    slug: string,
    projectId: string,
    issueId: string,
    config?: ApiRequestConfig,
  ): Promise<TIssueActivity[]> {
    return this.get(
      `/api/workspaces/${slug}/projects/${projectId}/work-items/${issueId}/history/`,
      config,
    );
  }

  /** Workspace-scoped activity feed — works for any work item in the workspace
   *  regardless of whether it has a project (e.g. project-less events). */
  async getWorkspaceWorkItemActivity(
    slug: string,
    issueId: string,
    config?: ApiRequestConfig,
  ): Promise<TIssueActivity[]> {
    return this.get(`/api/workspaces/${slug}/work-items/${issueId}/history/`, config);
  }

  async getIssueActivity(
    slug: string,
    projectId: string,
    issueId: string,
  ): Promise<TIssueActivity[]> {
    return this.getWorkItemActivity(slug, projectId, issueId);
  }

  async getSubscription(
    slug: string,
    projectId: string,
    issueId: string,
  ): Promise<{ subscribed: boolean }> {
    return this.get(
      `/api/workspaces/${slug}/projects/${projectId}/work-items/${issueId}/subscribe/`,
    );
  }

  async subscribe(slug: string, projectId: string, issueId: string): Promise<void> {
    return this.post(
      `/api/workspaces/${slug}/projects/${projectId}/work-items/${issueId}/subscribe/`,
    );
  }

  async unsubscribe(slug: string, projectId: string, issueId: string): Promise<void> {
    return this.delete(
      `/api/workspaces/${slug}/projects/${projectId}/work-items/${issueId}/subscribe/`,
    );
  }

  async listRelations(
    slug: string,
    projectId: string,
    issueId: string,
    config?: ApiRequestConfig,
  ): Promise<TIssueRelation> {
    const response = await this.get<TIssueRelationResponse>(
      `/api/workspaces/${slug}/projects/${projectId}/work-items/${issueId}/relations/`,
      config,
    );
    return normalizeIssueRelations(response);
  }

  async createRelations(
    slug: string,
    projectId: string,
    issueId: string,
    data: {
      relation_type?: "relates_to" | "blocked_by";
      subtype?: TDependencySubtype | null;
      related_list: Array<{
        relation_type: "relates_to" | "blocked_by";
        related_issue: string;
        subtype?: TDependencySubtype | null;
      }>;
    },
  ): Promise<TIssueRelation> {
    const response = await this.post<TIssueRelationResponse>(
      `/api/workspaces/${slug}/projects/${projectId}/work-items/${issueId}/relations/`,
      data,
    );
    return normalizeIssueRelations(response);
  }

  async listProjectRelations(
    slug: string,
    projectId: string,
    config?: ApiRequestConfig,
  ): Promise<TGanttDependency[]> {
    // Purpose-built lean read: every active relation edge for the project in
    // one response, instead of reconstructing edges from the (paginated)
    // work-item list — which also silently dropped dependencies past the
    // first page on large projects.
    const rows = await this.get<
      Array<{
        id: string;
        work_item_id: string;
        related_work_item_id: string;
        related_project_id: string | null;
        relation_type: string;
        subtype: TDependencySubtype | null;
      }>
    >(`/api/workspaces/${slug}/projects/${projectId}/work-item-relations/`, config);

    const ganttTypes = new Set<TGanttDependencyType>(["blocked_by", "relates_to"]);

    const deps: TGanttDependency[] = [];
    for (const entry of rows) {
      // The gantt only draws same-project edges; cross-project predecessors
      // are surfaced in the Predecessors column instead.
      if (entry.related_project_id !== projectId) continue;
      const type = entry.relation_type as TGanttDependencyType;
      if (!ganttTypes.has(type)) continue;
      const sourceIsParent = type === "blocked_by";
      deps.push({
        id: entry.id,
        sourceIssueId: sourceIsParent ? entry.related_work_item_id : entry.work_item_id,
        targetIssueId: sourceIsParent ? entry.work_item_id : entry.related_work_item_id,
        relation_type: type,
        subtype: entry.subtype ?? null,
      });
    }

    return deps;
  }

  async removeRelation(
    slug: string,
    projectId: string,
    issueId: string,
    relatedIssueId: string,
    relationType: TGanttDependencyType,
  ): Promise<void> {
    return this.post(
      `/api/workspaces/${slug}/projects/${projectId}/work-items/${issueId}/relations/remove/`,
      {
        relation_type: relationType,
        related_issue: relatedIssueId,
      },
    );
  }

  async setPredecessors(
    slug: string,
    projectId: string,
    issueId: string,
    sequenceIds: (number | string)[],
  ): Promise<{ predecessors: TIssuePredecessor[] }> {
    return this.put(
      `/api/workspaces/${slug}/projects/${projectId}/work-items/${issueId}/predecessors/`,
      { sequence_ids: sequenceIds },
    );
  }

  // A project-less work item (a standalone event or its Session) has its own
  // workspace-scoped link routes; a project item must use the project-scoped ones,
  // which the workspace routes refuse so project RBAC applies. Without the branch a
  // project-less item builds `…/projects//work-items/…` and 404s (NG-3443).
  private linksPath(slug: string, projectId: string | null | undefined, issueId: string): string {
    return projectId
      ? `/api/workspaces/${slug}/projects/${projectId}/work-items/${issueId}/links/`
      : `/api/workspaces/${slug}/work-items/${issueId}/links/`;
  }

  async listLinks(
    slug: string,
    projectId: string | null | undefined,
    issueId: string,
    config?: ApiRequestConfig,
  ): Promise<TIssueLink[]> {
    return this.get(this.linksPath(slug, projectId, issueId), config);
  }

  async createLink(
    slug: string,
    projectId: string | null | undefined,
    issueId: string,
    data: Partial<TIssueLink>,
  ): Promise<TIssueLink> {
    return this.post(this.linksPath(slug, projectId, issueId), data);
  }

  async updateLink(
    slug: string,
    projectId: string | null | undefined,
    issueId: string,
    linkId: string,
    data: Partial<TIssueLink>,
  ): Promise<TIssueLink> {
    return this.patch(`${this.linksPath(slug, projectId, issueId)}${linkId}/`, data);
  }

  async deleteLink(
    slug: string,
    projectId: string | null | undefined,
    issueId: string,
    linkId: string,
  ): Promise<void> {
    return this.delete(`${this.linksPath(slug, projectId, issueId)}${linkId}/`);
  }

  // Sub-work-items of a work item. A *project* work item must use the
  // project-scoped endpoint (the workspace-scoped one rejects project items so
  // project RBAC applies); a *project-less* work item uses the workspace-scoped
  // path. Both return the same envelope. Mirrors event.service's
  // createEventSession — without the branch, a project-less
  // item (project_id null → "") builds `…/projects//work-items/…` and 404s,
  // since the route's `<uuid:project_id>` can't match an empty segment (NG-610).
  private subWorkItemsPath(
    slug: string,
    projectId: string | null | undefined,
    issueId: string,
  ): string {
    return projectId
      ? `/api/workspaces/${slug}/projects/${projectId}/work-items/${issueId}/sub-work-items/`
      : `/api/workspaces/${slug}/work-items/${issueId}/sub-work-items/`;
  }

  async listSubWorkItems(
    slug: string,
    projectId: string | null | undefined,
    issueId: string,
    options?: {
      expandPermissions?: boolean;
      filters?: Readonly<Record<string, string>>;
      archived?: boolean;
    },
    config?: ApiRequestConfig,
  ): Promise<TIssueSubIssues> {
    // NG-3116: opt-in per-row delete/edit verdicts for children, so a nested row
    // selected in the project table's bulk bar carries the same signals as a
    // top-level row. Off by default keeps every existing caller's payload
    // byte-identical; the sub-task reads pass it behind the
    // nested-work-item-bulk-actions flag. Project-scoped only — only the
    // project-scoped sub-work-items endpoint honours the param, and the
    // workspace variant's children are never selected into that bar.
    const expand = options?.expandPermissions && projectId;
    // NG-3765: the project table's filters, applied to each child. Only the
    // project route reads them, as with `expand_permissions`.
    const filters = projectId ? options?.filters : undefined;
    // NG-3858: the Archived view reads the parent's archived children instead of
    // its active ones. Omitted when false, so an active read is byte-identical.
    const params = {
      ...(expand ? { expand_permissions: true } : {}),
      ...filters,
      ...(options?.archived ? { archived: true } : {}),
    };
    return this.get(
      this.subWorkItemsPath(slug, projectId, issueId),
      Object.keys(params).length > 0
        ? { ...config, params: { ...config?.params, ...params } }
        : config,
    );
  }

  /** Every listed parent's active descendants, keyed by each child's direct parent
   *  (NG-3731): the project Table's "Expand all", in one request instead of one
   *  per parent. At most 100 parents per call; `expandPermissions` as above. Each
   *  child arrives without `parent`, which comes once per parent under `parents`. */
  async listSubWorkItemTrees(
    slug: string,
    projectId: string,
    parentIds: readonly string[],
    options?: {
      expandPermissions?: boolean;
      filters?: Readonly<Record<string, string>>;
      archived?: boolean;
    },
    config?: ApiRequestConfig,
  ): Promise<{
    sub_issues_by_parent: Record<string, Omit<TIssue, "parent">[]>;
    parents: Record<string, TIssue["parent"]>;
  }> {
    return this.get(`/api/workspaces/${slug}/projects/${projectId}/sub-work-items/`, {
      ...config,
      params: {
        ...config?.params,
        parent_ids: parentIds.join(","),
        ...(options?.expandPermissions ? { expand_permissions: true } : {}),
        ...options?.filters,
        // NG-3858: the Archived view's tree read returns archived descendants.
        ...(options?.archived ? { archived: true } : {}),
      },
    });
  }

  async listSubIssues(
    slug: string,
    projectId: string | null | undefined,
    issueId: string,
  ): Promise<TIssueSubIssues> {
    return this.listSubWorkItems(slug, projectId, issueId);
  }

  async createSubWorkItem(
    slug: string,
    projectId: string | null | undefined,
    issueId: string,
    data: { sub_issue_ids: string[] },
  ): Promise<TIssueSubIssues> {
    return this.post(this.subWorkItemsPath(slug, projectId, issueId), data);
  }

  async createSubIssue(
    slug: string,
    projectId: string | null | undefined,
    issueId: string,
    data: { sub_issue_ids: string[] },
  ): Promise<TIssueSubIssues> {
    return this.createSubWorkItem(slug, projectId, issueId, data);
  }

  /** The importable field set the CSV wizard maps columns against (NG-3498):
   *  the core work-item columns with their per-type applicability and choice
   *  options, served by the same resolver the import validates rows with.
   *  Project-scoped because Stage comes from the project's own process. */
  async getWorkItemImportDefinition(
    slug: string,
    projectId: string,
  ): Promise<TWorkItemImportDefinition> {
    return this.get<TWorkItemImportDefinition>(
      `/api/workspaces/${slug}/projects/${projectId}/work-items/import-definition/`,
    );
  }

  /** The Review step's read-only pre-commit check (NG-3503): the real
   *  resolve/validate logic over the real workspace references, writing nothing.
   *  Same gate as {@link getWorkItemImportDefinition}. */
  async dryRunWorkItemImport(
    slug: string,
    projectId: string,
    rows: TWorkItemImportRowPayload[],
  ): Promise<TWorkItemImportDryRunResult> {
    return this.post<TWorkItemImportDryRunResult>(
      `/api/workspaces/${slug}/projects/${projectId}/work-items/import/dry-run/`,
      { rows },
    );
  }

  /** The Import click: one work item per valid row, each in its own savepoint.
   *  Partial success is the norm and is reported per row. */
  async commitWorkItemImport(
    slug: string,
    projectId: string,
    rows: TWorkItemImportRowPayload[],
  ): Promise<TWorkItemImportCommitResult> {
    return this.post<TWorkItemImportCommitResult>(
      `/api/workspaces/${slug}/projects/${projectId}/work-items/import/`,
      { rows },
    );
  }
}
